// The envelope's peephole reveal (sprint 28 item 3, roadmaps 63 and 64). The server decides everything (game/engine.js, message t:'mail');
// this file only shows it: a pure plan of beats (times in ms from the moment the reveal card has landed), and a small controller that
// plays the plan on #mail. Looks are client/mail-art.js and client/mail.css (placeholders, door-artist replaces them). Sound goes through
// sfx(event), the page's existing sound settings: a muted client plays nothing. Reduced motion: the result at once, the sound still plays.
import { buildMailParts, lampText, keyringText, shieldText, sessionText, STEPS } from './mail-art.js';

const FELL = 'A key fragment fell.';

export const COUNT_STEPS = 3, COUNT_MS = 300;
export const RESULT_SOUND = { points: 'tick', shield: 'tick', big: 'mail-big', jackpot: 'mail-gilded' };
export const TRAY_MS = 10000, RESULT_HOLD_MS = 4000, QUIET_MS = 4000;

// The beats for a tier, from design item3-design.md: plain about 3 s, big 3.6 s with a held shake and one flash, gilded 4.6 s to the result, the count done by 4.9 s (never over 5 s).
// A beat is { at, kind, sound? }. kinds: seep slide lamp peephole shake zoom seal count result flash.
export function mailPlan({ tier, reduced = false }) {
  const sound = RESULT_SOUND[tier] ?? 'tick';
  if (reduced) return [{ at: 0, kind: 'result', sound }];
  const counting = tier === 'points' || tier === 'big' || tier === 'jackpot';
  let plan;
  if (tier === 'jackpot') {
    plan = [{ at: 0, kind: 'seep' }, { at: 900, kind: 'slide', sound: 'paper' }, { at: 900, kind: 'lamp', sound: 'tick' }, { at: 1600, kind: 'peephole' },
      { at: 2400, kind: 'shake', sound: 'thunk' }, { at: 3300, kind: 'zoom' }, { at: 3800, kind: 'seal', sound: 'seal' }, { at: 4600, kind: 'result', sound }, { at: 4600, kind: 'flash' }];
  } else if (tier === 'big') {
    plan = [{ at: 700, kind: 'slide', sound: 'paper' }, { at: 700, kind: 'lamp', sound: 'tick' }, { at: 1500, kind: 'peephole' },
      { at: 2100, kind: 'shake', sound: 'thunk' }, { at: 2700, kind: 'zoom' }, { at: 3400, kind: 'seal', sound: 'seal' }, { at: 3600, kind: 'result', sound }, { at: 3600, kind: 'flash' }];
  } else {
    plan = [{ at: 700, kind: 'slide', sound: 'paper' }, { at: 700, kind: 'lamp', sound: 'tick' }, { at: 1500, kind: 'peephole' },
      { at: 2100, kind: 'zoom' }, { at: 2100, kind: 'seal', sound: 'seal' }, { at: 2800, kind: 'result', sound: tier === 'shield' ? 'seal' : undefined }];
  }
  if (counting) {
    const r = plan.find(b => b.kind === 'result');
    r.count = true;
    if (tier === 'points') r.sound = undefined;
    for (let i = 0; i <= COUNT_STEPS; i++) plan.push({ at: r.at + Math.round(COUNT_MS * i / COUNT_STEPS), kind: 'count', frac: i / COUNT_STEPS, sound: tier === 'points' && i > 0 ? 'tick' : undefined });
  }
  return plan.sort((a, b) => a.at - b.at);
}
export const planEnd = plan => plan.reduce((m, b) => Math.max(m, b.at), 0);

const STATE = { seep: 'seep', slide: 'slide', peephole: 'peephole', shake: 'shake', zoom: 'zoom', result: 'result' }; // the seal is a sound, not a state

// root: the #mail element. sfx(event): plays a sound or nothing. onOpen({ skipped }): the envelope was seen (finished, skipped or collapsed).
export function createMail({ root, sfx = () => {}, onOpen = () => {}, set = (fn, ms) => setTimeout(fn, ms), clear = id => clearTimeout(id), doc = root.ownerDocument }) {
  const p = buildMailParts(doc);
  root.replaceChildren(p.tray, p.stage);
  root.dataset.state = 'idle'; root.hidden = true;
  let timers = [], run = null, trayTimer = null;
  const later = (fn, ms) => { const id = set(fn, ms); timers.push(id); return id; };
  const clearAll = () => { for (const t of timers) clear(t); timers = []; };

  function tray(msg) {
    const l = msg.lamp ?? { n: 0, mult: 1 };
    p.lamp.textContent = lampText(l); p.keyring.textContent = keyringText(msg.fragments ?? 0); p.shield.textContent = shieldText(msg.shield ?? 0);
    root.dataset.tray = '1'; root.hidden = false;
  }
  // the session score counts the envelope's points, so with an envelope it is written when that opens, never before (no spoiled count)
  const scored = msg => { p.session.textContent = sessionText(msg.session); };
  const lastLine = text => { p.last.textContent = text ? `Last: ${text}` : ''; clear(trayTimer); if (text) trayTimer = set(() => { p.last.textContent = ''; }, TRAY_MS); };

  function finish(skipped) {
    if (!run || run.done) return;
    run.done = true;
    clearAll();
    root.dataset.state = 'result'; scored(run.msg);
    p.result.textContent = run.msg.mail.text;
    root.dataset.running = '';
    onOpen({ skipped });
    if (!run.hold) later(() => { if (run?.done) { root.dataset.state = 'idle'; lastLine(run.msg.mail.text); } }, RESULT_HOLD_MS);
  }
  function apply(beat, msg) {
    const mail = msg.mail;
    switch (beat.kind) {
      case 'seep': root.dataset.gold = '1'; root.dataset.state = 'seep'; break;
      case 'lamp': tray(msg); break;
      case 'count': p.result.textContent = beat.frac >= 1 ? mail.text : mail.tier === 'shield' ? mail.text : `${Math.round(mail.points * beat.frac)} points`; break;
      case 'flash': root.dataset.flash = '1'; later(() => { root.dataset.flash = ''; }, 120); break;
      case 'result': root.dataset.state = 'result'; scored(msg); if (mail.tier === 'jackpot') root.dataset.gold = '1'; p.result.textContent = beat.count ? '0 points' : mail.text; break;
      default: root.dataset.state = STATE[beat.kind] ?? root.dataset.state;
    }
    if (beat.sound) sfx(beat.sound);
  }

  const api = {
    get running() { return !!run && !run.done; },
    // msg: the server's t:'mail'. at: hold the reveal still that far in (a demo frame). reduced: the result at once.
    show(msg, { reduced = false, at } = {}) {
      clearAll(); clear(trayTimer);
      tray(msg);
      root.dataset.gold = ''; root.dataset.flash = '';
      if (!msg.mail) { // nothing dropped: the tray only, and the one quiet line when the cap was hit
        scored(msg); run = null; root.dataset.state = 'idle'; root.dataset.running = '';
        if (msg.capped) { p.last.textContent = 'No more envelopes for now.'; trayTimer = set(() => { p.last.textContent = ''; }, QUIET_MS); }
        else if (msg.fell) { p.last.textContent = `Last: ${FELL} Keyring ${msg.fragments} of ${STEPS}.`; trayTimer = set(() => { p.last.textContent = ''; }, TRAY_MS); }
        else p.last.textContent = '';
        return;
      }
      root.dataset.tier = msg.mail.tier; root.dataset.running = '1';
      p.last.textContent = '';
      const plan = mailPlan({ tier: msg.mail.tier, reduced });
      run = { msg, done: false, hold: at !== undefined };
      const ended = planEnd(plan);
      if (at !== undefined) { // a held frame: everything up to that moment at once, nothing after
        for (const b of plan.filter(b => b.at <= at)) apply({ ...b, sound: undefined }, msg);
        if (at >= ended) { run.done = true; root.dataset.running = ''; }
        return;
      }
      for (const b of plan) { if (b.at === 0) apply(b, msg); else later(() => apply(b, msg), b.at); }
      later(() => finish(false), ended);
      if (reduced) finish(false);
    },
    // a tap: straight to the result
    skip() {
      if (!api.running) return false;
      const msg = run.msg;
      clearAll();
      sfx(RESULT_SOUND[msg.mail.tier] ?? 'tick');
      finish(true);
      return true;
    },
    // the next knock comes while the envelope is still on: the result is kept in the tray
    collapse() {
      if (!run) return;
      const text = run.msg.mail?.text;
      if (api.running) finish(false);
      clearAll(); root.dataset.state = 'idle'; root.dataset.flash = '';
      lastLine(text);
    },
    update: tray,
  };
  root.addEventListener('click', () => api.skip());
  return api;
}
